import { useEffect, useMemo, useState } from "react";
import api from "../../../services/ApiService";
import { ApiResponse } from "../../../models/ApiResponse";
import { BaseCodeHierarchy } from "../../../models/BaseCodeHierarchy";
import { Box, Button, IconButton } from "@mui/material";
import { toast } from "react-toastify";
import { SessionDetails } from "../../../models/SessionDetails";
import BaseCodeModal from "./BaseCodeModal";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteValidationModal from "../../../components/ManageSettingComponents/DeleteValidationModal";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableContainer,
  useMaterialReactTable,
} from "material-react-table";
import { hover } from "@testing-library/user-event/dist/hover";
import SessionService from "../../../services/SessionService";
import { Permissions } from "../../../models/Enum";

export default function () {
  const [baseCodeHierarchiesList, setBaseCodeHierarchiesList] = useState<
    BaseCodeHierarchy[]
  >([]);
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);

  const BaseCodeHierarchyGridCollumns = useMemo<
    MRT_ColumnDef<BaseCodeHierarchy>[]
  >(
    () => [
      { accessorKey: "id", header: "Id", width: 100 },
      { accessorKey: "rank", header: "Rank", width: 100 },
      {
        accessorKey: "attachTypeDisplayText",
        header: "Attachment Type",
        width: 350,
      },
    ],
    []
  );

  const manageAppSetting = SessionService.getInstance().hasPermission(Permissions.ManageAppSettings);

  const table = useMaterialReactTable({
    columns: BaseCodeHierarchyGridCollumns,
    initialState: { columnVisibility: { id: false }},
    data: baseCodeHierarchiesList,
    enablePagination: false,
    enableRowActions: manageAppSetting,
    positionActionsColumn: "last",
    enableRowOrdering: manageAppSetting,
    enableSorting: false,
    enableFilters: false,
    enableColumnActions: false,
    renderRowActions: ({ row }) => (
      <Box style={{ width: "150px" }}>
        <IconButton onClick={() => showDeleteModalOpen(row.original)}>
          <DeleteIcon />
        </IconButton>
      </Box>
    ),
    muiRowDragHandleProps: ({ table }) => ({
      onDragEnd: async () => {
        const { draggingRow, hoveredRow } = table.getState();
        if (hoveredRow && draggingRow) {
          baseCodeHierarchiesList.splice(
            (hoveredRow as MRT_Row<BaseCodeHierarchy>).index,
            0,
            baseCodeHierarchiesList.splice(draggingRow.index, 1)[0]
          );
          setBaseCodeHierarchiesList([...baseCodeHierarchiesList]);
          await updateRanks(baseCodeHierarchiesList);
        }
      },
    }),
  });

  const updateRanks = async (reOrderedList) => {
    let rank = 1;
    reOrderedList.forEach((baseCodeItem) => {
      baseCodeItem.rank = rank;
      rank++;
    });

    const resp = await api.put<ApiResponse<boolean>>(
      "/Picklist/basecode-hierarchies/update-ranks",
      { bcImageHierarchies: reOrderedList }
    );
    if (resp?.isSuccess) {
      toast.success("Rank updated successfully");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const getBaseCodeHierarchyRepsonse = await api.get<
      ApiResponse<BaseCodeHierarchy[]>
    >("/Picklist/basecode-hierarchies");
    setBaseCodeHierarchiesList(getBaseCodeHierarchyRepsonse?.data || []);
  };

  const showDeleteModalOpen = (row: any) => {
    setDeleteModalOpen(true);
    formik.setValues({ ...row });
  };

  const handleCloseModal = () => {
    setShowAddUpdateModal(false);
    setDeleteModalOpen(false);
    formik.resetForm();
  };

  const deleteSubmit = () => {
    api
      .delete<ApiResponse<SessionDetails>>(
        `/Picklist/basecode-hierarchy/${formik.values.id}`
      )
      .then(async (response: any) => {
        if (response.isSuccess) {
          setDeleteModalOpen(false);
          formik.resetForm();
          toast.success(response.message);
          await fetchData();
        } else {
          throw new Error(response.message);
        }
      })
      .catch(() => {});
  };

  const formik = useFormik({
    initialValues: {
      id: 0,
      rank: 0,
      attachType: "",
      isActive: true,
    },
    validationSchema: Yup.object({
      rank: Yup.number()
        .required("Rank is Required")
        .min(1, "Rank must be greater than 0")
        .test(
          "check-rank-duplicate",
          "Rank already exists",
          function async(value) {
            return new Promise((resolve) => {
              const isRankDuplicate = baseCodeHierarchiesList.some(
                (item) => item.rank === value && item.id !== formik.values.id
              );
              isRankDuplicate ? resolve(false) : resolve(true);
            });
          }
        ),
      attachType: Yup.string()
        .trim()
        .required("Attachment Type is Required")
        .min(1, "Attachment Type must be at least 1 characters")
        .max(40, "Attachment Type must be at most 40 characters")
        .test(
          "check-attach-type-duplicate",
          "Attachment type already exists",
          function async(value) {
            return new Promise((resolve) => {
              const isAttachDuplicate = baseCodeHierarchiesList.some(
                (item) =>
                  item.attachType &&
                  item.attachType === value &&
                  item.id !== formik.values.id
              );
              isAttachDuplicate ? resolve(false) : resolve(true);
            });
          }
        ),
    }),
    onSubmit: async (values) => {
      setDisableSubmit(true); // Set disableSubmit to true when the form is submitted
      if (values.id > 0) {
        api
          .put<ApiResponse<number>>("/Picklist/basecode-hierarchy", {
            id: values.id,
            rank: values.rank,
            attachType: values.attachType.trim(),
            isActive: JSON.parse(`${values.isActive}`),
          })
          .then(async (response: any) => {
            if (response.isSuccess) {
              setShowAddUpdateModal(false);
              formik.resetForm();
              toast.success(response.message);
              await fetchData();
            } else {
              throw new Error(response.message);
            }
          })
          .catch(() => {});
      } else {
        api
          .post<ApiResponse<number>>("/Picklist/basecode-hierarchy", {
            rank: values.rank,
            attachType: values.attachType,
            isActive: JSON.parse(`${values.isActive}`),
          })
          .then(async (response: any) => {
            if (response.isSuccess) {
              setShowAddUpdateModal(false);
              formik.resetForm();
              toast.success(response.message);
              await fetchData();
            } else {
              throw new Error(response.message);
            }
          })
          .catch(() => {});
      }
    },
  });

  return (
    <>
      <div>
        {manageAppSetting && <Button
          variant="contained"
          onClick={() => {
            setShowAddUpdateModal(true);
            setDisableSubmit(false);
          }}
        >
          Include Attachment Type
        </Button>}
        {showAddUpdateModal && (
          <BaseCodeModal
            isOpen={showAddUpdateModal}
            onClose={handleCloseModal}
            formik={formik}
            isButtonDisabled={disableSubmit}
          />
        )}
        <DeleteValidationModal
          addModalOpen={deleteModalOpen}
          handleCloseModal={handleCloseModal}
          deleteSubmit={deleteSubmit}
          fieldIdentifier={"Basecode Rank"}
        />
      </div>
      <MRT_TableContainer table={table} style={{ marginTop: "10px" }} />
    </>
  );
}
